export const toUpperCase = <T extends string>(str: T): Uppercase<T> =>
  str.toUpperCase() as Uppercase<T>

export const toLowerCase = <T extends string>(str: T): Lowercase<T> =>
  str.toLowerCase() as Lowercase<T>

export const toTitleCase = (str: string): string => {
  return str.replace(/[_-]/g, " ").replace(/\b\w/g, char => char.toUpperCase())
}

/**
 * Question words that typically start questions
 * Includes both base words and common contractions
 */
const QUESTION_WORDS = [
  // Base question words
  "what",
  "who",
  "when",
  "where",
  "why",
  "how",
  "which",
  "whose",
  "whom",
  "can",
  "could",
  "would",
  "should",
  "will",
  "do",
  "does",
  "did",
  "is",
  "are",
  "was",
  "were",
  "has",
  "have",
  "had",
  // Common contractions (with apostrophes stripped)
  "whats", // what's
  "whatre", // what're
  "whatd", // what'd
  "whatll", // what'll
  "whos", // who's
  "whod", // who'd
  "wholl", // who'll
  "whens", // when's
  "wheres", // where's
  "whered", // where'd
  "wherell", // where'll
  "whys", // why's
  "whyd", // why'd
  "whyll", // why'll
  "hows", // how's
  "howd", // how'd
  "howll", // how'll
  "whichs", // which's (less common)
  "whichd", // which'd (less common)
  "whichll", // which'll (less common)
  "cant", // can't
  "couldnt", // couldn't
  "wouldnt", // wouldn't
  "shouldnt", // shouldn't
  "wont", // won't
  "dont", // don't
  "doesnt", // doesn't
  "didnt", // didn't
  "isnt", // isn't
  "arent", // aren't
  "wasnt", // wasn't
  "werent", // weren't
  "hasnt", // hasn't
  "havent", // haven't
  "hadnt", // hadn't
] as const

export type IsQuestionOptions = {
  /**
   * Minimum number of words required to consider as a question
   * @default 2
   */
  minWords?: number
}

/**
 * Determines if a given string is likely a question based on:
 * 1. Starting with common question words (handles contractions like "what's", "how's")
 * 2. Meeting minimum word count requirement
 * 3. Optionally ending with a question mark
 *
 * @param input - The string to check
 * @param options - Configuration options
 * @returns true if the string appears to be a question
 *
 * @example
 * ```typescript
 * isQuestion("What is the weather today?") // true
 * isQuestion("What's the floor price?") // true (handles contractions)
 * isQuestion("How are you") // true (even without question mark)
 * isQuestion("Hello world") // false
 * isQuestion("What is", { minWords: 3 }) // false (only 2 words)
 * ```
 */
// Regex for splitting words, defined at module level for performance
const WORD_SPLIT_REGEX = /\s+/

export function isQuestion(
  input: string,
  options: IsQuestionOptions = {},
): boolean {
  const { minWords = 2 } = options

  const trimmedInput = input.trim()
  if (!trimmedInput) {
    return false
  }

  // Split into words and filter out empty strings
  const words = trimmedInput
    .toLowerCase()
    .split(WORD_SPLIT_REGEX)
    .filter(word => word.length > 0)

  // Check minimum word count
  if (words.length < minWords) {
    return false
  }

  // Check if starts with a question word
  // Strip apostrophes to handle contractions like "what's", "what're", etc.
  const firstWord = words[0].replace(/'/g, "")
  const startsWithQuestionWord = QUESTION_WORDS.includes(
    firstWord as (typeof QUESTION_WORDS)[number],
  )

  // Check if ends with question mark
  const endsWithQuestionMark = trimmedInput.endsWith("?")

  // Consider it a question if it starts with a question word OR ends with question mark
  // But prioritize question words since that's more reliable for natural language
  return startsWithQuestionWord || endsWithQuestionMark
}
